import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Brand } from "./Brand";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Copy, ExternalLink, Code2, Sparkles, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const ASSETS = ["BTC", "ETH", "USDT", "USDC", "SOL", "XRP", "ADA", "DOGE", "MATIC", "DOT"];

export default function EmbedGenerator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cfg, setCfg] = useState({
    asset: "USDC",
    amount: 500,
    primaryColor: "#0A3B2C",
    logo: "",
    partnerId: "",
    wallet: "",
    userId: "",
  });
  const [partners, setPartners] = useState([]);
  const [partnerName, setPartnerName] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/v1/partners").then((r) => setPartners(r.data.partners || [])).catch(()=>{});
  }, [user]);

  const baseUrl = window.location.origin;

  const previewUrl = useMemo(() => {
    const q = new URLSearchParams();
    if (cfg.asset) q.set("asset", cfg.asset);
    if (cfg.amount) q.set("amount", String(cfg.amount));
    if (cfg.primaryColor) q.set("primaryColor", cfg.primaryColor);
    if (cfg.logo) q.set("logo", cfg.logo);
    if (cfg.partnerId) q.set("partner_id", cfg.partnerId);
    if (cfg.wallet) q.set("wallet", cfg.wallet);
    if (cfg.userId) q.set("user_id", cfg.userId);
    return `${baseUrl}/widget?${q.toString()}`;
  }, [cfg, baseUrl]);

  const snippet = useMemo(() => (
`<iframe
  src="${previewUrl}"
  width="420"
  height="700"
  frameborder="0"
  allow="camera; clipboard-write"
  style="border-radius:16px;border:0;box-shadow:0 12px 40px rgba(10,59,44,0.06);">
</iframe>`
  ), [previewUrl]);

  const registerPartner = async () => {
    if (!user) { toast.error("Sign in first"); return; }
    if (!partnerName) { toast.error("Partner name required"); return; }
    setRegistering(true);
    try {
      const r = await api.post("/v1/partners", { name: partnerName, webhook_url: webhookUrl || null });
      setPartners([r.data, ...partners]);
      setCfg({ ...cfg, partnerId: r.data.id });
      setPartnerName(""); setWebhookUrl("");
      toast.success("Partner registered — API key generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    } finally { setRegistering(false); }
  };

  return (
    <div className="xch-app-bg min-h-screen">
      <header className="xch-ink sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="embed-brand-btn"><Brand size={28} theme="dark" /></button>
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-white hover:text-white hover:bg-white/10" data-testid="embed-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to widget
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 bg-accent/10 text-accent rounded-full px-3 py-1 text-xs font-semibold mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Partner program
          </div>
          <h1 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight text-primary">
            Embed Xchangely in your site
          </h1>
          <p className="text-sm md:text-base text-slate-500 mt-2 max-w-2xl">
            One iframe, full on-ramp. Customize the asset, amount, branding, and pass your user ID for reconciliation.
            Earn revenue share on every completed order.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_440px] gap-6">
          {/* Controls */}
          <div className="space-y-5">
            <section className="xch-card p-5 md:p-6 space-y-4" data-testid="embed-controls">
              <h2 className="font-display text-lg font-bold text-primary">Widget configuration</h2>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Default asset">
                  <Select value={cfg.asset} onValueChange={(v) => setCfg({ ...cfg, asset: v })}>
                    <SelectTrigger className="rounded-xl" data-testid="embed-asset-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ASSETS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Default amount (EUR)">
                  <Input
                    type="number" min={20} value={cfg.amount}
                    onChange={(e) => setCfg({ ...cfg, amount: Number(e.target.value) })}
                    className="rounded-xl" data-testid="embed-amount-input"
                  />
                </Field>
              </div>

              <Field label="Primary color">
                <div className="flex items-center gap-2">
                  <input
                    type="color" value={cfg.primaryColor}
                    onChange={(e) => setCfg({ ...cfg, primaryColor: e.target.value })}
                    className="w-12 h-10 rounded-xl border border-slate-200 cursor-pointer bg-white"
                    data-testid="embed-color-picker"
                  />
                  <Input
                    value={cfg.primaryColor}
                    onChange={(e) => setCfg({ ...cfg, primaryColor: e.target.value })}
                    className="rounded-xl font-mono"
                  />
                </div>
              </Field>

              <Field label="Logo URL (optional)">
                <Input
                  placeholder="https://yourdomain.com/logo.png" value={cfg.logo}
                  onChange={(e) => setCfg({ ...cfg, logo: e.target.value })}
                  className="rounded-xl" data-testid="embed-logo-input"
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Partner ID">
                  <Input
                    placeholder="abc123" value={cfg.partnerId}
                    onChange={(e) => setCfg({ ...cfg, partnerId: e.target.value })}
                    className="rounded-xl font-mono" data-testid="embed-partner-input"
                  />
                </Field>
                <Field label="Your user ID (optional)">
                  <Input
                    placeholder="user-456" value={cfg.userId}
                    onChange={(e) => setCfg({ ...cfg, userId: e.target.value })}
                    className="rounded-xl font-mono" data-testid="embed-userid-input"
                  />
                </Field>
              </div>

              <Field label="Pre-attached wallet (optional, locks the field)">
                <Input
                  placeholder="bc1q... / 0x... / etc." value={cfg.wallet}
                  onChange={(e) => setCfg({ ...cfg, wallet: e.target.value })}
                  className="rounded-xl font-mono text-xs" data-testid="embed-wallet-input"
                />
              </Field>
            </section>

            {/* Partner registration */}
            <section className="xch-card p-5 md:p-6 space-y-4" data-testid="partner-registration">
              <div>
                <h2 className="font-display text-lg font-bold text-primary">Register partner & webhook</h2>
                <p className="text-xs text-slate-500 mt-1">
                  Receive POST to your webhook when an order is completed. Retries 3× with exponential backoff.
                </p>
              </div>
              {user ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Partner name">
                      <Input value={partnerName} onChange={(e) => setPartnerName(e.target.value)} className="rounded-xl" data-testid="partner-name-input" />
                    </Field>
                    <Field label="Webhook URL">
                      <Input
                        placeholder="https://api.yoursite.com/xch/webhook"
                        value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)}
                        className="rounded-xl font-mono text-xs" data-testid="partner-webhook-input"
                      />
                    </Field>
                  </div>
                  <Button
                    onClick={registerPartner} disabled={registering}
                    className="bg-accent hover:bg-accent/90 text-white rounded-xl"
                    data-testid="partner-register-btn"
                  >
                    {registering ? "Registering…" : "Register & generate API key"}
                  </Button>

                  {partners.length > 0 && (
                    <div className="space-y-2 pt-3 border-t border-slate-100" data-testid="partner-list">
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Your partners</div>
                      {partners.map((p) => (
                        <div key={p.id} className="bg-slate-50 rounded-xl p-3 text-sm" data-testid={`partner-row-${p.id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-display font-bold text-primary">{p.name}</div>
                            <button
                              onClick={() => setCfg({ ...cfg, partnerId: p.id })}
                              className="text-xs text-accent font-semibold hover:underline"
                              data-testid={`partner-use-${p.id}`}
                            >Use this →</button>
                          </div>
                          <div className="font-mono text-[11px] text-slate-500 mt-1 truncate">id: {p.id}</div>
                          <div className="font-mono text-[11px] text-slate-500 truncate">key: {p.api_key}</div>
                          {p.webhook_url && <div className="font-mono text-[11px] text-slate-500 truncate">→ {p.webhook_url}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-slate-500 bg-slate-50 rounded-xl p-3">
                  Sign in via the widget to register a partner account and receive webhooks.
                </div>
              )}
            </section>
          </div>

          {/* Snippet + preview */}
          <div className="space-y-5">
            <section className="xch-card p-5 md:p-6 space-y-3" data-testid="embed-snippet-card">
              <div className="flex items-center gap-2">
                <Code2 className="w-4 h-4 text-accent" />
                <h2 className="font-display text-base font-bold text-primary">Embed snippet</h2>
              </div>
              <pre className="bg-primary text-white text-[11px] font-mono rounded-xl p-3 overflow-x-auto whitespace-pre-wrap break-all" data-testid="embed-snippet">{snippet}</pre>
              <div className="flex gap-2">
                <Button
                  onClick={() => { navigator.clipboard.writeText(snippet); toast.success("Snippet copied"); }}
                  className="flex-1 bg-accent hover:bg-accent/90 text-white rounded-xl gap-1.5"
                  data-testid="embed-copy-btn"
                ><Copy className="w-4 h-4" /> Copy snippet</Button>
                <Button
                  variant="outline" onClick={() => window.open(previewUrl, "_blank")}
                  className="rounded-xl gap-1.5"
                  data-testid="embed-open-preview-btn"
                ><ExternalLink className="w-4 h-4" /> Open</Button>
              </div>
            </section>

            <section className="xch-card p-3 overflow-hidden" data-testid="embed-preview-card">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 px-2 pt-1 pb-2">Live preview</div>
              <iframe
                key={previewUrl}
                src={previewUrl}
                title="Xchangely widget preview"
                width="100%" height="700"
                className="rounded-2xl border-0 bg-background"
                allow="camera; clipboard-write"
                data-testid="embed-preview-iframe"
              />
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</Label>
    {children}
  </div>
);
